import moment from 'moment';
import formatCurrency from 'format-currency';

class InformationManagementController {
  constructor($rootScope, $http, toastr, $state, AuthenticationService, $stateParams, $uibModal) {
    "ngInject";
    let self = this;
    self.toastr = toastr;
    self.$http = $http;
    self.$rootScope = $rootScope;

    this.employees = [];
    this.selectedUser = null;
    this.userId = null;
    this.fromDate = null;
    this.endDate = null;

    this.sourceChargeCodes = [];
    this.destinationChargeCodes = [];
    this.selectedSourceChargeCode = null;
    this.selectedDestinationChargeCode = null;
    this.loadingSourceChargeCodes = false;
    this.loadingDestinationChargeCodes = false;

    this.opened = {};
    this.datePickerFormat = 'MMM dd, yyyy';
    this.datePickerOptions = {
      formatYear: 'yy',
      startingDay: 1,
      showWeeks: false
    };

    $http.get($rootScope.apiHost + "/api/admin/users/names", {withCredentials: true}).then(function (success) {
      self.employees = success.data;
    });
  }

  currency(value) {
    if (value == null || value === '') {
      return '—';
    }
    return '$' + formatCurrency(Number(value));
  }

  open($event, elementOpened) {
    $event.preventDefault();
    $event.stopPropagation();
    this.opened[elementOpened] = true;
  }

  selectUser(user) {
    this.selectedUser = user;
    this.userId = user && user.id;
    this.selectedSourceChargeCode = null;
    this.selectedDestinationChargeCode = null;
    this.sourceChargeCodes = [];
    this.destinationChargeCodes = [];
    this.loadDestinationChargeCodes();
    this.loadSourceChargeCodes();
  }

  onDateChanged() {
    this.selectedSourceChargeCode = null;
    this.sourceChargeCodes = [];
    this.loadSourceChargeCodes();
  }

  hasRequiredCriteria() {
    return !!(this.userId && this.fromDate && this.endDate
      && moment(this.fromDate).isValid()
      && moment(this.endDate).isValid()
      && !moment(this.fromDate).isAfter(moment(this.endDate), 'day'));
  }

  formatApiDate(date) {
    return moment(date).format('YYYY-MM-DD');
  }

  loadSourceChargeCodes() {
    if (!this.hasRequiredCriteria()) {
      this.sourceChargeCodes = [];
      return;
    }

    let self = this;
    this.loadingSourceChargeCodes = true;
    const fromDate = this.formatApiDate(this.fromDate);
    const toDate = this.formatApiDate(this.endDate);

    this.$http.get(
      this.$rootScope.apiHost + '/api/admin/chargecodes/used?userId=' + this.userId
        + '&fromDate=' + fromDate
        + '&toDate=' + toDate,
      { withCredentials: true }
    ).then(function (success) {
      self.sourceChargeCodes = success.data || [];
      if (self.selectedSourceChargeCode) {
        const stillPresent = self.sourceChargeCodes.some(function (code) {
          return code.id === self.selectedSourceChargeCode.id;
        });
        if (!stillPresent) {
          self.selectedSourceChargeCode = null;
        }
      }
    }, function (failure) {
      self.sourceChargeCodes = [];
      self.toastr.error(
        (failure.data && failure.data.message) ? failure.data.message : 'Could not load source charge codes.',
        'ERROR'
      );
    }).finally(function () {
      self.loadingSourceChargeCodes = false;
    });
  }

  loadDestinationChargeCodes() {
    if (!this.userId) {
      this.destinationChargeCodes = [];
      return;
    }

    let self = this;
    this.loadingDestinationChargeCodes = true;
    this.$http.get(
      this.$rootScope.apiHost + '/api/admin/users/' + this.userId,
      { withCredentials: true }
    ).then(function (success) {
      self.destinationChargeCodes = (success.data && success.data.availableChargeCodes)
        ? success.data.availableChargeCodes.slice().sort(function (a, b) {
          return String(a.name || '').localeCompare(String(b.name || ''));
        })
        : [];
      if (self.selectedDestinationChargeCode) {
        const stillPresent = self.destinationChargeCodes.some(function (code) {
          return code.id === self.selectedDestinationChargeCode.id;
        });
        if (!stillPresent) {
          self.selectedDestinationChargeCode = null;
        }
      }
    }, function (failure) {
      self.destinationChargeCodes = [];
      self.toastr.error(
        (failure.data && failure.data.message) ? failure.data.message : 'Could not load destination charge codes.',
        'ERROR'
      );
    }).finally(function () {
      self.loadingDestinationChargeCodes = false;
    });
  }

  selectSourceChargeCode(code) {
    this.selectedSourceChargeCode = code;
  }

  selectDestinationChargeCode(code) {
    this.selectedDestinationChargeCode = code;
  }

  canPerformBulkUpdate() {
    return this.hasRequiredCriteria()
      && this.selectedSourceChargeCode
      && this.selectedDestinationChargeCode
      && this.selectedSourceChargeCode.id !== this.selectedDestinationChargeCode.id;
  }

  getBulkUpdateBlockedMessage() {
    if (!this.userId) {
      return 'Select an employee.';
    }
    if (!this.fromDate || !this.endDate) {
      return 'Select a start and end date.';
    }
    if (moment(this.fromDate).isAfter(moment(this.endDate), 'day')) {
      return 'Start date must be on or before end date.';
    }
    if (!this.selectedSourceChargeCode) {
      return 'Select a source charge code.';
    }
    if (!this.selectedDestinationChargeCode) {
      return 'Select a destination charge code.';
    }
    if (this.selectedSourceChargeCode.id === this.selectedDestinationChargeCode.id) {
      return 'Source and destination charge codes must be different.';
    }
    return '';
  }

  resetBulkForm() {
    this.selectedUser = null;
    this.userId = null;
    this.fromDate = null;
    this.endDate = null;
    this.sourceChargeCodes = [];
    this.destinationChargeCodes = [];
    this.selectedSourceChargeCode = null;
    this.selectedDestinationChargeCode = null;
  }

  bulkUpdateChargeCode() {
    const blockedMessage = this.getBulkUpdateBlockedMessage();
    if (blockedMessage) {
      this.toastr.warning(blockedMessage, 'WARNING');
      return;
    }

    const fromDate = this.formatApiDate(this.fromDate);
    const toDate = this.formatApiDate(this.endDate);
    let self = this;

    this.$http.post(
      this.$rootScope.apiHost + '/api/admin/chargecodes/bulkchange'
        + '?userId=' + this.userId
        + '&fromDate=' + fromDate
        + '&toDate=' + toDate
        + '&oldChargeCode=' + this.selectedSourceChargeCode.id
        + '&newChargeCode=' + this.selectedDestinationChargeCode.id,
      {},
      { withCredentials: true }
    ).then(function (success) {
      const message = (success.data && success.data.data) ? success.data.data : 'Charge codes updated.';
      self.toastr.success(message, 'SUCCESS');
      self.resetBulkForm();
    }, function (failure) {
      self.toastr.error(
        (failure.data && failure.data.message) ? failure.data.message : 'Could not update charge codes.',
        'ERROR'
      );
    });
  }
}

export default InformationManagementController;



// WEBPACK FOOTER //
// ./client/pages/admin/information-management/information-management.controller.js